import React from 'react';
import { motion } from 'framer-motion';
import { Promotion, PromotionStatus } from '../types';
import { Calendar, AlertCircle, CheckCircle2 } from 'lucide-react';
interface TimelineViewProps {
  promotions: Promotion[];
  onEdit: (id: string) => void;
}
export function TimelineView({
  promotions,
  onEdit
}: TimelineViewProps) {
  // Sort by deadline
  const sortedPromotions = [...promotions].sort((a, b) => new Date(a.deadline).getTime() - new Date(b.deadline).getTime());
  // Group by month
  const grouped = sortedPromotions.reduce((acc, promo) => {
    const date = new Date(promo.deadline);
    const key = date.toLocaleString('default', {
      month: 'long',
      year: 'numeric'
    });
    if (!acc[key]) acc[key] = [];
    acc[key].push(promo);
    return acc;
  }, {} as Record<string, Promotion[]>);
  return <div className="max-w-3xl mx-auto py-8" data-id="element-185">
      <div className="absolute left-8 md:left-1/2 top-0 bottom-0 w-px bg-gray-200 -z-10 hidden md:block" data-id="element-186" />

      {Object.entries(grouped).map(([month, items], groupIndex) => <div key={month} className="mb-12 relative" data-id="element-187">
          <div className="sticky top-20 z-10 flex justify-center mb-8" data-id="element-188">
            <span className="bg-white border border-gray-200 px-4 py-1 rounded-full text-sm font-semibold text-gray-600 shadow-sm" data-id="element-189">
              {month}
            </span>
          </div>

          <div className="space-y-8" data-id="element-190">
            {items.map((item, index) => {
          const deadline = new Date(item.deadline);
          const today = new Date();
          const daysLeft = Math.ceil((deadline.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
          const isExpired = daysLeft < 0;
          const isUrgent = daysLeft > 0 && daysLeft <= 14;
          return <motion.div key={item.id} initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: index * 0.1
          }} className="relative md:grid md:grid-cols-2 md:gap-8 items-center" data-id="element-191">
                  {/* Dot on timeline */}
                  <div className={`absolute left-[-29px] md:left-1/2 md:-ml-1.5 w-3 h-3 rounded-full border-2 bg-white z-10
                    ${isExpired ? 'border-gray-300' : isUrgent ? 'border-orange-500' : 'border-blue-500'}
                  `} data-id="element-192" />

                  {/* Date (Left side on desktop for alternate items, or right side) */}
                  <div className="md:text-right mb-2 md:mb-0" data-id="element-193">
                    <div className="inline-flex items-center gap-2 text-sm font-mono text-gray-500 bg-gray-50 px-2 py-1 rounded" data-id="element-194">
                      <Calendar className="w-3 h-3" data-id="element-195" />
                      {deadline.toLocaleDateString()}
                    </div>
                    <div className={`text-xs font-bold mt-1 ${isUrgent ? 'text-orange-600' : 'text-gray-400'}`} data-id="element-196">
                      {isExpired ? 'Expired' : `${daysLeft} days left`}
                    </div>
                  </div>

                  {/* Card Content */}
                  <div onClick={() => onEdit(item.id)} className={`
                      bg-white p-4 rounded-xl border shadow-sm cursor-pointer hover:shadow-md transition-all
                      ${isUrgent ? 'border-orange-200 bg-orange-50/30' : 'border-gray-200'}
                      ${item.status === 'completed' ? 'opacity-60 grayscale' : ''}
                    `} data-id="element-197">
                    <div className="flex justify-between items-start" data-id="element-198">
                      <div data-id="element-199">
                        <h4 className="font-bold text-gray-900" data-id="element-200">
                          {item.bankName}
                        </h4>
                        <p className="text-sm text-gray-600" data-id="element-201">
                          {item.promotionName}
                        </p>
                      </div>
                      <span className="font-mono font-bold text-green-600 bg-green-50 px-2 py-1 rounded text-sm" data-id="element-202">
                        ${item.bonusAmount}
                      </span>
                    </div>
                    {item.status === 'completed' && <div className="mt-2 flex items-center gap-1 text-xs text-green-600 font-medium" data-id="element-203">
                        <CheckCircle2 className="w-3 h-3" data-id="element-204" /> Completed
                      </div>}
                    {isUrgent && <div className="mt-2 flex items-center gap-1 text-xs text-orange-600 font-medium" data-id="element-205">
                        <AlertCircle className="w-3 h-3" data-id="element-206" /> Deadline approaching
                      </div>}
                  </div>
                </motion.div>;
        })}
          </div>
        </div>)}
    </div>;
}